// One company's health in the platform console (D-182, D-183): its status and the reasons in words, its onboarding
// checklist, this week's numbers against its usual week, its last four weeks, and what the platform knows of it
// (Voxowa's own, a quiet spell, a note). Its team stays hidden until asked for, and each look is recorded: names,
// roles, the last four digits and their phones, for support; never the company's customers or calls.
import { type FormEvent, useState } from "react";
import { useSession } from "../app/session";
import { Card, ErrorNote, Field, Loading, Stat, useAction, useLoad } from "../app/ui";
import {
  appVersions,
  change,
  integrationActivity,
  integrationLogo,
  integrationName,
  integrationState,
  phoneLine,
  share,
  teamProblems,
  USES_LABEL,
} from "../lib/companyHealth";
import { USE_CASES } from "../lib/integrations";
import { fmtNumber, roleLabel } from "../lib/format";
import { ago } from "../lib/teamHealth";
import type { CompanyHealthDetail, CompanyTeamMember } from "../lib/types";
import { shortDate, StatusBadges } from "./PlatformHome";

export function CompanyHealthTab({ orgId }: { orgId: string }) {
  const { api } = useSession();
  const health = useLoad(() => api.platformCompanyHealth(orgId), [api, orgId]);
  const h = health.data;

  return (
    <div className="stack">
      <ErrorNote error={health.error} />
      {health.loading && !h && <Loading />}
      {h && (
        <>
          <Card title="Status" actions={<StatusBadges c={h} />}>
            {h.reasons.length > 0
              ? (
                <ul className="reasons">
                  {h.reasons.map((r) => <li key={r}>{r}</li>)}
                </ul>
              )
              : (
                <p className="faint">
                  {h.status === "onboarding"
                    ? `Day ${h.days_live + 1} of its first 14: it is judged against its usual week after that.`
                    : h.status === "suspended"
                    ? "Suspended: nothing is judged while it is."
                    : "Calling and following up as usual."}
                </p>
              )}
            {h.note && <p className="faint">Note: {h.note}</p>}
          </Card>

          {h.status === "onboarding" && (
            <Card title="Getting started">
              <ul className="checklist">
                {h.checklist.map((x) => <li key={x.key} className={x.done ? "done" : ""}>{x.label}</li>)}
              </ul>
            </Card>
          )}

          <Numbers h={h} />
          <Trend h={h} />
          <Integrations h={h} />
          <Flags key={`${h.internal}-${h.quiet_until}-${h.note}`} h={h} onDone={health.reload} />
          <Team orgId={orgId} plan={h.features} />
        </>
      )}
    </div>
  );
}

function Numbers({ h }: { h: CompanyHealthDetail }) {
  const m = h.metrics;
  const now = new Date();
  return (
    <div className="grid cols-4">
      <Stat
        label="Calls this week"
        value={fmtNumber(m.calls_7)}
        hint={m.calls_usual === null
          ? "No usual week yet"
          : `${change(m.calls_7, m.calls_usual)} · usually ${fmtNumber(m.calls_usual)}`}
      />
      <Stat
        label="People calling"
        value={`${m.active_7} of ${m.callers}`}
        hint={m.active_usual === null ? "callers this week" : `usually ${m.active_usual}`}
      />
      <Stat
        label="Follow-ups done"
        value={fmtNumber(m.done_7)}
        hint={`${share(m.on_time_7, m.done_7)} on time this week`}
      />
      <Stat
        label="Follow-ups overdue"
        value={fmtNumber(m.overdue)}
        alert={m.overdue >= 50 && m.overdue > m.done_7}
      />
      <Stat
        label="What happened noted"
        value={share(m.outcomes_7, m.connected_calls_7)}
        hint={`of ${fmtNumber(m.connected_calls_7)} connected calls`}
      />
      <Stat
        label="Phones synced today"
        value={`${m.phones_synced_24h} of ${m.phones}`}
        alert={m.phones > 0 && m.phones_synced_24h < m.phones}
      />
      <Stat
        label="Owner or manager"
        value={m.manager_seen_at ? ago(m.manager_seen_at, now) : "Not seen"}
        hint="last on the dashboard"
        alert={m.manager_days_away >= 7}
      />
      <Stat
        label="New customers"
        value={fmtNumber(m.leads_7)}
        hint={`this week · ${fmtNumber(m.crm_actions_7)} CRM actions`}
      />
    </div>
  );
}

/** Calls a day over the last four weeks; days off are dimmed. */
function Trend({ h }: { h: CompanyHealthDetail }) {
  const max = Math.max(1, ...h.days.map((d) => d.calls));
  return (
    <Card title="Last four weeks">
      <div className="bars" role="img" aria-label="Calls per day over the last four weeks">
        {h.days.map((d) => (
          <div
            key={d.day}
            className={d.working ? "" : "off"}
            title={`${shortDate(d.day)}: ${d.calls} calls${d.working ? "" : " (day off)"}${
              d.overdue === null ? "" : `, ${d.overdue} overdue`
            }`}
            style={{ height: `${(d.calls / max) * 100}%` }}
          />
        ))}
      </div>
      <div className="row between faint">
        <span>{shortDate(h.days[0].day)}</span>
        <span>Calls a day; days off are dimmed</span>
        <span>{shortDate(h.days[h.days.length - 1].day)}</span>
      </div>
    </Card>
  );
}

/** Its integrations and what each brought in, and what it asked Voxowa for (D-184). States and counts only. */
function Integrations({ h }: { h: CompanyHealthDetail }) {
  const now = new Date();
  const useCase = (key: string) => USE_CASES.find((u) => u.key === key)?.label.toLowerCase() ?? key;
  return (
    <Card title="Integrations">
      {h.integrations.length === 0 && <p className="faint">None set up.</p>}
      {h.integrations.length > 0 && (
        <div className="list">
          {h.integrations.map((i) => {
            const state = integrationState(i);
            return (
              <div className="list-item" key={i.key}>
                <div className="row between">
                  <span className="row" style={{ gap: 8 }}>
                    {integrationLogo(i.key) && <img src={integrationLogo(i.key)!} alt="" width={20} height={20} />}
                    <strong>{integrationName(i.key)}</strong>
                  </span>
                  <span className={`badge ${state.tone}`}>{state.label}</span>
                </div>
                <div className="faint">
                  {i.connected_at && `Since ${shortDate(i.connected_at.slice(0, 10))} · `}
                  {integrationActivity(i, now)}
                </div>
                {i.error && <div className="error-note" style={{ marginTop: 6 }}>{i.error}</div>}
              </div>
            );
          })}
        </div>
      )}
      {h.requested.length > 0 && (
        <p className="faint" style={{ marginTop: 12, marginBottom: 0 }}>
          Asked Voxowa for: {h.requested.map((r) => `${integrationName(r.key)} (${useCase(r.use_case)})`).join(", ")}
        </p>
      )}
    </Card>
  );
}

/** Voxowa's own company, a quiet spell until a date, and a note: what the platform knows of it. */
function Flags({ h, onDone }: { h: CompanyHealthDetail; onDone: () => Promise<void> }) {
  const { api } = useSession();
  const { busy, run } = useAction();
  const [internal, setInternal] = useState(h.internal);
  const [quiet, setQuiet] = useState(h.quiet_until ?? "");
  const [note, setNote] = useState(h.note ?? "");
  const changed = internal !== h.internal || quiet !== (h.quiet_until ?? "") || note.trim() !== (h.note ?? "");

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const ok = await run(
      () => api.platformSetCompanyFlags(h.org_id, { internal, quietUntil: quiet || null, note: note.trim() || null }),
      "Saved",
    );
    if (ok) await onDone();
  };

  return (
    <Card title="What we know">
      <form onSubmit={submit}>
        <label className="row" style={{ marginBottom: 12 }}>
          <input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} />
          <span>
            Voxowa's own company <span className="faint">(tracked, but never counted in the totals)</span>
          </span>
        </label>
        <div className="grid cols-2">
          <Field label="Quiet until" hint="A known quiet spell, such as someone on leave. Its status shows it.">
            <input type="date" value={quiet} onChange={(e) => setQuiet(e.target.value)} />
          </Field>
          <Field label="Note" hint="For the platform only; its people never see it.">
            <input maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} />
          </Field>
        </div>
        <div className="actions">
          <button className="primary" disabled={busy || !changed}>Save</button>
        </div>
      </form>
    </Card>
  );
}

/** The company's team, hidden until asked for (D-183): each look is recorded in its history. */
function Team({ orgId, plan }: { orgId: string; plan: CompanyHealthDetail["features"] }) {
  const { api } = useSession();
  const { busy, run } = useAction();
  const [team, setTeam] = useState<CompanyTeamMember[] | null>(null);
  const now = new Date();

  const open = async () => {
    await run(async () => setTeam(await api.platformCompanyTeam(orgId)));
  };

  if (!team) {
    return (
      <Card title="Team">
        <div className="team-hidden">
          <span className="faint">
            Hidden until you need it, to help someone with a problem. Each look is recorded in the company's history. It
            shows names, roles, the last four digits of their numbers and their phones; never their customers or calls.
          </span>
          <button disabled={busy} onClick={open}>Show team</button>
        </div>
      </Card>
    );
  }

  const versions = appVersions(team);
  return (
    <Card title="Team" actions={<button className="small" onClick={() => setTeam(null)}>Hide</button>}>
      {versions.length > 0 && (
        <p className="faint">
          App versions: {versions.map((v) => `${v.version} on ${v.phones} ${v.phones === 1 ? "phone" : "phones"}`)
            .join(", ")}
        </p>
      )}
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Person</th>
              <th>Phone</th>
              <th>Uses</th>
              <th className="num">Calls this week</th>
              <th className="num">Follow-ups</th>
              <th>Problems</th>
            </tr>
          </thead>
          <tbody>
            {team.map((m) => {
              const problems = teamProblems(m, plan, now);
              return (
                <tr key={m.member_id}>
                  <td style={{ minWidth: 150 }}>
                    <div className="primary">{m.name}</div>
                    <div className="faint">
                      {roleLabel[m.role]}
                      {m.phone_last4 && ` · ··${m.phone_last4}`}
                    </div>
                  </td>
                  <td style={{ minWidth: 190 }}>
                    {phoneLine(m)}
                    {m.phone && <div className="faint">Synced {ago(m.phone.last_sync_at, now)}</div>}
                  </td>
                  <td>
                    {USES_LABEL[m.uses]}
                    {m.dashboard_at && <div className="faint">Dashboard {ago(m.dashboard_at, now)}</div>}
                  </td>
                  <td className="num">
                    {fmtNumber(m.calls_7)}
                    {m.calls_usual !== null && <div className="faint">usually {fmtNumber(m.calls_usual)}</div>}
                  </td>
                  <td className="num">
                    {fmtNumber(m.done_7)} done
                    <div className="faint">{fmtNumber(m.overdue)} overdue</div>
                  </td>
                  <td style={{ minWidth: 240 }}>
                    {problems.length === 0
                      ? <span className="faint">None</span>
                      : (
                        <ul className="reasons" style={{ margin: 0 }}>
                          {problems.map((p) => <li key={p}>{p}</li>)}
                        </ul>
                      )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
